import { useEffect, useState, useMemo, useCallback } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import { Controller, useForm } from "react-hook-form";
import { useSearchWithParams, useParams as useParamsHook } from "@/hooks/useSearchParams";
import usePagination from "@/hooks/usePagination";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { Alumni, AlumniQueryVariables } from "./types";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useQuery } from "@apollo/client";
import { ALUMNI_QUERY } from "./queries";
import { useParams } from "react-router-dom";
import { buildColumns, mapAlumni } from "./_utils";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import { PROGRAM_VERSION_FILTER_OPTIONS } from "../../../_shared/constants";
import { ExpandedProgramLevelsRow } from "../../../_shared/_components/ExpandedProgramLevelsRow";

const parseSelectedPrograms = (selectedPrograms: string[]) => {
  const curriculumVersions: string[] = [];

  selectedPrograms.forEach((program) => {
    const [version] = program.split(" ");

    if (version && !curriculumVersions.includes(version)) {
      curriculumVersions.push(version);
    }
  });

  return {
    curriculumVersions: curriculumVersions.length > 0 ? curriculumVersions : undefined
  };
};

export default function UniversityAlumniPage() {
  const { universityId } = useParams();
  const { shouldShowPersonProfileActions } = useUniversityStaffPermissions();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { isCompactLayout } = useIsCompactLayout();
  const [selectedAlumni, setSelectedAlumni] = useState<Alumni | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);

  const { params, addSearchParam, deleteSearchParams } = useParamsHook();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });

  const parsedEnrollmentDateRange = useMemo(() => {
    if (!params.enrollmentDateRange) return undefined;
    if (typeof params.enrollmentDateRange === "string") {
      const [from, to] = params.enrollmentDateRange.split(",");
      return { from, to };
    }
    return params.enrollmentDateRange as { from: string; to: string };
  }, [params.enrollmentDateRange]);

  const initialSelectedPrograms = useMemo(
    () => (Array.isArray(params.programs) ? params.programs : params.programs ? [params.programs] : []),
    [params.programs]
  );

  const initialCurriculumVersions = useMemo(() => {
    const { curriculumVersions } = parseSelectedPrograms(initialSelectedPrograms);
    return curriculumVersions;
  }, [initialSelectedPrograms]);

  const [variables, setVariables] = useState<AlumniQueryVariables>({
    input: {
      universityLegacyIds: [universityId?.toString() || ""],
      alumni: true,
      search: params.searchTerm || undefined,
      enrollmentDateRange: parsedEnrollmentDateRange,
      curriculumVersions: initialCurriculumVersions
    }
  });

  const filtersForm = useForm<{ enrollmentDateRange: { startDate: Date | null; endDate: Date | null } | null }>({
    defaultValues: {
      enrollmentDateRange: parsedEnrollmentDateRange
        ? { startDate: new Date(parsedEnrollmentDateRange.from), endDate: new Date(parsedEnrollmentDateRange.to) }
        : null
    }
  });

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(initialSelectedPrograms);

  useEffect(() => {
    const { curriculumVersions } = parseSelectedPrograms(selectedPrograms);
    setVariables((prevVariables) => ({
      input: {
        ...prevVariables.input,
        curriculumVersions
      }
    }));
  }, [selectedPrograms]);

  useEffect(() => {
    setVariables((prevVariables) => ({
      input: {
        ...prevVariables.input,
        search: debouncedSearch || undefined
      }
    }));
  }, [debouncedSearch]);

  const { data, loading } = useQuery(ALUMNI_QUERY, {
    variables: {
      ...variables,
      ...paginationVariables
    }
  });

  const alumni = mapAlumni(
    data?.universityStudents || {
      totalCount: 0,
      pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: undefined, endCursor: undefined },
      nodes: []
    }
  );

  const handleViewAlumni = useCallback((alumni: Alumni) => {
    setSelectedAlumni(alumni);
    setIsDetailsPanelOpen(true);
  }, []);

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        handleViewAlumni
      }),
    [isCompactLayout, handleViewAlumni]
  );

  const filterState = {
    programs: selectedPrograms.length > 0,
    dates: !!(variables.input?.enrollmentDateRange?.from && variables.input?.enrollmentDateRange?.to)
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setSelectedPrograms([]);
    filtersForm.reset({ enrollmentDateRange: null });
    setVariables((prevVariables) => ({
      input: {
        ...prevVariables.input,
        search: undefined,
        curriculumVersions: undefined,
        levels: undefined,
        enrollmentDateRange: undefined
      }
    }));
    deleteSearchParams("programs[]");
    deleteSearchParams("enrollmentDateRange");
    clearSearch();
    resetPagination();
  };

  return (
    <>
      <TableHeading
        heading="PGM Program Alumni"
        body="Search for all graduated students associated with this PGM University Program"
      />
      <Table
        data={alumni}
        loading={loading}
        columns={columns}
        data-testid="alumni-table"
        onClickRow={(row) => {
          handleViewAlumni(row.original);
        }}
        renderExpandedRow={({ row }) => {
          return <ExpandedProgramLevelsRow row={row} />;
        }}
        hasActiveFilters={!!searchValue || activeFilterCount > 0}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No PGM Program Alumni"
            caption="There are no alumni associated with this PGM University Program"
            iconName="school"
          />
        )}
        renderControls={() => (
          <TableControls
            pagination={createHeaderPagination(data?.universityStudents)}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Alumni"
              query={searchValue}
              onClear={clearSearch}
              onChange={setSearchValue}
            />
            <div data-name="programs">
              <MultiSelect
                label="Program Version"
                options={PROGRAM_VERSION_FILTER_OPTIONS}
                className="w-full z-50"
                selectedValues={selectedPrograms}
                onSelect={(value, checked) => {
                  const updatedPrograms = checked
                    ? [...selectedPrograms, value]
                    : selectedPrograms.filter((v) => v !== value);

                  setSelectedPrograms(updatedPrograms);

                  const { curriculumVersions } = parseSelectedPrograms(updatedPrograms);

                  setVariables((prevVariables) => ({
                    input: {
                      ...prevVariables.input,
                      curriculumVersions
                    }
                  }));

                  const paramValue = updatedPrograms.join(",");
                  addSearchParam("programs[]", paramValue);
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedPrograms([]);
                  setVariables((prevVariables) => ({
                    input: {
                      ...prevVariables.input,
                      curriculumVersions: undefined
                    }
                  }));
                  deleteSearchParams("programs[]");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="dates" data-filter-size="custom">
              <Controller
                name="enrollmentDateRange"
                control={filtersForm.control}
                render={({ field }) => (
                  <DateSelect
                    key={field.value ? `date-${field.value.startDate}-${field.value.endDate}` : "date-empty"}
                    label="Enrollment Date Range"
                    placeholder="Show All"
                    useModal={true}
                    popoverDirection="down"
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      if (typeof e === "object" && e !== null && e.startDate && e.endDate) {
                        const startDate =
                          e.startDate instanceof Date
                            ? e.startDate.toISOString().split("T")[0]
                            : e.startDate.toString();
                        const endDate =
                          e.endDate instanceof Date ? e.endDate.toISOString().split("T")[0] : e.endDate.toString();

                        setVariables((prevVariables) => ({
                          input: {
                            ...prevVariables.input,
                            enrollmentDateRange: { from: startDate, to: endDate }
                          }
                        }));
                        addSearchParam("enrollmentDateRange", [e.startDate, e.endDate].join(","));
                        resetPagination();
                      } else if (
                        e === null ||
                        (typeof e === "object" && e !== null && e.startDate === null && e.endDate === null)
                      ) {
                        setVariables((prevVariables) => ({
                          input: {
                            ...prevVariables.input,
                            enrollmentDateRange: undefined
                          }
                        }));
                        deleteSearchParams("enrollmentDateRange");
                        resetPagination();
                      }
                    }}
                  />
                )}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.universityStudents)} />}
      />

      <PersonSideSheet
        open={isDetailsPanelOpen && !!selectedAlumni}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={selectedAlumni?.pgaId ?? null}
        title="Alumni Details"
        customActionButtons={
          selectedAlumni && shouldShowPersonProfileActions
            ? [
                {
                  text: "View Person",
                  variant: "outlined" as const,
                  onClick: () => {
                    window.open(`/people/${selectedAlumni.pgaId}/person/overview`, "_blank");
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
